import Link from 'next/link';
import React, { FC } from 'react';
import { BsPencil, BsPersonVcard } from 'react-icons/bs';
import { IoPeopleOutline, IoSettingsOutline, IoSwapHorizontal } from 'react-icons/io5';
import { TbLogout } from 'react-icons/tb';

import { Menu, Text, UnstyledButton } from '@mantine/core';

import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { Avatar } from 'components/avatar/Avatar';
import { AdminZone } from 'components/header/components/AdminZone';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useCurrentUser } from 'hooks/use-current-user';
import { useTeams } from 'hooks/use-teams';
import { AuthenticationService } from 'services/AuthenticationService';
import { RouterService } from 'services/RouterService';
import { TeamService } from 'services/TeamService';

import cx from './UserIcon.module.scss';

export const UserIcon: FC = () => {
    const user = useCurrentUser();
    const { teams } = useTeams();
    const currentTeam = useCurrentTeam();
    const { companies } = useCurrentTeamCompanies();

    if (!user) {
        return null;
    }

    const handleLogout = async () => {
        await AuthenticationService.logout();
    };

    const switchTeam = () => {
        TeamService.openSwitchTeamModal({
            create: {
                title: 'Create Team',
            },
            switchy: {
                title: 'Switch Team',
            },
        });
    };

    const companyUrl =
        companies.length === 1 ? CompanyHelpers.urls.view(companies[0].slug) : CompanyHelpers.urls.manage();

    return (
        <Menu trigger="click-hover" position="bottom-end" offset={3} withinPortal={false}>
            <Menu.Target>
                <UnstyledButton className={cx.root}>
                    <Avatar user={user} size="sm" />

                    <div className={cx.name}>
                        {user.name}

                        {teams.length > 1 && (
                            <Text fz="xs" c="dimmed" mt={-3}>
                                {currentTeam?.name}
                            </Text>
                        )}
                    </div>
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item component={Link} href="/account#account" leftSection={<IoSettingsOutline size={14} />}>
                    Account
                </Menu.Item>

                <Menu.Item
                    component={Link}
                    href={TeamHelpers.urls.settings(currentTeam?.id)}
                    leftSection={<IoPeopleOutline size={14} />}
                >
                    Team & Subscription
                </Menu.Item>

                {teams.length > 1 && (
                    <Menu.Item leftSection={<IoSwapHorizontal size={14} />} onClick={switchTeam}>
                        Switch Team
                    </Menu.Item>
                )}

                <Menu.Divider />

                {!!companies.length && (
                    <Menu.Item
                        leftSection={<BsPersonVcard size={14} />}
                        onClick={async () => {
                            await RouterService.push(companyUrl);
                        }}
                    >
                        My PRO Profile
                    </Menu.Item>
                )}

                <Menu.Item
                    leftSection={<BsPencil size={14} />}
                    onClick={async () => {
                        await RouterService.push(ProjectHelpers.urls.overview());
                    }}
                >
                    My Design Projects
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item leftSection={<TbLogout size={14} />} c="red" onClick={handleLogout}>
                    Logout
                </Menu.Item>

                <AdminZone />
            </Menu.Dropdown>
        </Menu>
    );
};
